// Add or edit a member note (architecture sprint-12 §9.2). Member is a select of CURRENT members (locked when
// opened from a member row), Date defaults to today and cannot be in the future (the viewer's timezone),
// Description is trimmed, 1–2000 characters, with a counter. Sprint 15: authors edit their own notes too (the API
// enforces §5.3); only an Admin may move a staff note to another member (subjectLocked for everyone else).
import { todayIn, type LocalDate } from '@shared/calendar';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useNoteShiftOptions } from '@/hooks/use-attendance';
import { useCreateNote, useUpdateNote } from '@/hooks/use-notes';
import { ApiClientError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { timeRange } from '@/lib/shift-format';
import { NOTE_MAX_LENGTH, type Note, type NoteSubject } from '@/types/notes';

export const FUTURE_DATE_MESSAGE = "The date can't be in the future.";

interface NoteDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timezone: string;
  /** Current members (former members are never offered). */
  subjects: readonly NoteSubject[];
  /** Opened from a member row: pre-selected and locked. */
  lockedSubject?: { id: string; name: string } | null;
  /** Admin edit of an existing note. */
  note?: Note | null;
  /**
   * Sprint 13 (§13): show the optional Shift field (an Admin, or a Supervisor holding viewAttendance on the
   * project). The options are the member's shifts on that day; the API re-checks everything.
   */
  canLinkShift?: boolean;
  /** Sprint 13: Attendance "Add note" pre-fills the shift's day and the shift. */
  initialDate?: LocalDate;
  initialShiftId?: string;
  /** Sprint 15 (A-7): editing keeps the member (non-Admin authors, and every self-reported note). */
  subjectLocked?: boolean;
}

export function NoteDialog({
  projectId,
  open,
  onOpenChange,
  timezone,
  subjects,
  lockedSubject = null,
  note = null,
  canLinkShift = false,
  initialDate,
  initialShiftId,
  subjectLocked = false,
}: NoteDialogProps): JSX.Element {
  const create = useCreateNote(projectId);
  const update = useUpdateNote(projectId);
  const today: LocalDate = todayIn(timezone);
  const [userId, setUserId] = useState('');
  const [date, setDate] = useState<string>(today);
  const [description, setDescription] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset only when the dialog opens or its target changes (ids, not object identity: parents re-render).
  const noteId = note?.id ?? null;
  const lockedId = lockedSubject?.id ?? null;
  useEffect(() => {
    if (!open) return;
    setUserId(note?.subject.id ?? lockedId ?? '');
    setDate(note?.date ?? initialDate ?? todayIn(timezone));
    setDescription(note?.description ?? '');
    setShiftId(note?.shift?.id ?? initialShiftId ?? '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, noteId, lockedId, timezone, initialDate, initialShiftId]);

  const shiftOptions = useNoteShiftOptions(projectId, userId, date, open && canLinkShift);
  // The stored shift stays listed (marked voided) when it no longer qualifies, so editing keeps it visible.
  const storedShift = note?.shift ?? null;
  const shiftChoices = [
    ...(shiftOptions.data ?? []),
    ...(storedShift && !(shiftOptions.data ?? []).some((s) => s.id === storedShift.id) ? [storedShift] : []),
  ];

  const trimmed = description.trim();
  const pending = create.isPending || update.isPending;
  const options = subjects.filter((s) => s.current || s.id === note?.subject.id);

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!userId) return setError('Choose a member.');
    if (!date) return setError('Choose a date.');
    if (date > today) return setError(FUTURE_DATE_MESSAGE);
    if (trimmed.length === 0) return setError('Write a note.');
    const onError = (err: Error): void => setError(err instanceof ApiClientError ? err.message : 'Something went wrong. Please try again.');
    const chosenShift = canLinkShift && shiftId !== '' ? shiftId : null;
    if (note) {
      const patch = {
        ...(!subjectLocked && userId !== note.subject.id ? { userId } : {}),
        ...(date !== note.date ? { date } : {}),
        ...(trimmed !== note.description ? { description: trimmed } : {}),
        ...(canLinkShift && chosenShift !== (note.shift?.id ?? null) ? { shiftId: chosenShift } : {}),
      };
      if (Object.keys(patch).length === 0) return onOpenChange(false);
      update.mutate({ noteId: note.id, patch }, { onSuccess: () => (toast.success('Note saved.'), onOpenChange(false)), onError });
      return;
    }
    create.mutate({ userId, date, description: trimmed, ...(chosenShift ? { shiftId: chosenShift } : {}) }, { onSuccess: () => (toast.success('Note added.'), onOpenChange(false)), onError });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{note ? 'Edit note' : 'Add note'}</DialogTitle>
          <DialogDescription>{note ? 'Every change is kept in the note history.' : 'A note about one member of this project.'}</DialogDescription>
        </DialogHeader>
        <form id="note-form" onSubmit={onSubmit} className="space-y-3" noValidate>
          <div className="grid gap-1.5">
            <Label htmlFor="note-member">Member</Label>
            {lockedSubject && !note ? (
              <Input id="note-member" value={lockedSubject.name} readOnly className="bg-muted" />
            ) : note && subjectLocked ? (
              <Input id="note-member" value={note.subject.name} readOnly className="bg-muted" />
            ) : (
              <NativeSelect id="note-member" value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">Choose a member</option>
                {options.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="note-date">Date</Label>
            <Input id="note-date" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          {canLinkShift && (
            <div className="grid gap-1.5">
              <Label htmlFor="note-shift">Shift</Label>
              <NativeSelect id="note-shift" value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
                <option value="">None</option>
                {shiftChoices.map((s) => (
                  <option key={s.id} value={s.id}>
                    {timeRange(s.startedAt, s.endedAt, timezone).replace('–', ' – ')}
                    {s.voided ? ' (voided)' : ''}
                  </option>
                ))}
              </NativeSelect>
            </div>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="note-description">Description</Label>
            <textarea
              id="note-description"
              rows={5}
              maxLength={NOTE_MAX_LENGTH}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              aria-describedby="note-counter"
              className="flex w-full resize-y rounded-lg border border-input bg-field px-3 py-2 text-base leading-6 text-foreground placeholder:text-faint transition-colors duration-150 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring sm:text-sm sm:leading-5"
            />
            <p id="note-counter" className={cn('text-right text-xs tabular-nums', description.length >= NOTE_MAX_LENGTH ? 'text-warning-fg' : 'text-muted-foreground')}>
              {description.length} / {NOTE_MAX_LENGTH}
            </p>
          </div>
          {error && (
            <p role="alert" className="text-sm text-danger-fg">
              {error}
            </p>
          )}
        </form>
        <DialogFooter>
          <Button type="submit" form="note-form" className="flex-1" disabled={pending}>
            {pending ? 'Saving…' : note ? 'Save' : 'Add note'}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
